import axios from 'axios';

export class IPFSService {
  private gateway: string;
  private pinataJwt?: string;
  private pinataUrl = 'https://api.pinata.cloud/pinning/pinFileToIPFS';

  constructor(gateway?: string, pinataJwt?: string) {
    this.gateway = gateway || 'https://gateway.pinata.cloud/ipfs/';
    this.pinataJwt = pinataJwt;
  }

  async uploadFile(file: File, metadata?: Record<string, any>): Promise<string> {
    if (!this.pinataJwt) {
      throw new Error('Pinata JWT token is required for file uploads');
    }

    const formData = new FormData();
    formData.append('file', file);

    if (metadata) {
      formData.append('pinataMetadata', JSON.stringify({
        name: file.name,
        keyvalues: metadata
      }));
    }

    const response = await axios.post(this.pinataUrl, formData, {
      headers: {
        'Authorization': `Bearer ${this.pinataJwt}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data.IpfsHash;
  }

  async uploadJSON(data: any, name: string, metadata?: Record<string, any>): Promise<string> {
    if (!this.pinataJwt) {
      throw new Error('Pinata JWT token is required for uploads');
    }

    const response = await axios.post(
      'https://api.pinata.cloud/pinning/pinJSONToIPFS',
      {
        pinataContent: data,
        pinataMetadata: {
          name,
          keyvalues: metadata || {}
        }
      },
      {
        headers: {
          'Authorization': `Bearer ${this.pinataJwt}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.IpfsHash;
  }

  async retrieveFile(ipfsHash: string): Promise<Blob> {
    const url = `${this.gateway}${ipfsHash}`;
    const response = await axios.get(url, {
      responseType: 'blob'
    });
    return response.data;
  }

  async retrieveJSON<T = any>(ipfsHash: string): Promise<T> {
    const url = `${this.gateway}${ipfsHash}`;
    const response = await axios.get<T>(url);
    return response.data;
  }

  async unpin(ipfsHash: string): Promise<boolean> {
    if (!this.pinataJwt) {
      throw new Error('Pinata JWT token is required');
    }

    try {
      await axios.delete(
        `https://api.pinata.cloud/pinning/unpin/${ipfsHash}`,
        {
          headers: {
            'Authorization': `Bearer ${this.pinataJwt}`
          }
        }
      );
      return true;
    } catch (error) {
      console.error('Failed to unpin:', error);
      return false;
    }
  }

  getGatewayUrl(ipfsHash: string): string {
    return `${this.gateway}${ipfsHash}`;
  }

  setGateway(gateway: string) {
    this.gateway = gateway.endsWith('/') ? gateway : `${gateway}/`;
  }
} 